import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { contactQueries } from "@/hooks/useContacts.tsx";
import type { Contact } from "@/types/contact.ts";

export const SCHEDULING_RECENT_CONTACTS_KEY = "scheduling_recent_contacts";
const MAX_STORED = 10;
const MAX_DISPLAYED = 3;

/** Returns the most recently selected contact IDs (most recent first, up to 3). */
export function getRecentContactIds(): string[] {
  try {
    const raw = localStorage.getItem(SCHEDULING_RECENT_CONTACTS_KEY);
    if (!raw) return [];
    const ids: unknown = JSON.parse(raw);
    if (!Array.isArray(ids)) return [];
    return ids.filter((id): id is string => typeof id === "string").slice(0, MAX_DISPLAYED);
  } catch {
    return [];
  }
}

/** Fetch recent contacts individually so they're available even if beyond the first page. */
export function useRecentContacts(): {
  recentIds: string[];
  recentContacts: Contact[];
} {
  const recentIds = useMemo(() => getRecentContactIds(), []);
  const recentContacts = useQueries({
    queries: recentIds.map((id) => contactQueries.detail(id)),
    combine: (results) => results.filter((r) => r.data != null).map((r) => r.data as Contact),
  });
  return { recentIds, recentContacts };
}

/** Records a contact selection, moving it to the front of the recents list. */
export function addRecentContact(contactId: string): void {
  try {
    const raw = localStorage.getItem(SCHEDULING_RECENT_CONTACTS_KEY);
    const existing: string[] = raw ? JSON.parse(raw) : [];
    const deduped = [contactId, ...existing.filter((id) => id !== contactId)];
    localStorage.setItem(
      SCHEDULING_RECENT_CONTACTS_KEY,
      JSON.stringify(deduped.slice(0, MAX_STORED)),
    );
  } catch {
    // localStorage unavailable — silently ignore
  }
}
